import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Navigation, MapPin, StopCircle, Play, X, Car } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GPSMileageTracker = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [tripPurpose, setTripPurpose] = useState('');
  const [currentTrip, setCurrentTrip] = useState({
    startTime: null,
    currentMiles: 0,
    positions: []
  });
  const [contractorId, setContractorId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const watchIdRef = useRef(null);

  useEffect(() => {
    // Get user info
    const role = localStorage.getItem('user_role');
    const id = localStorage.getItem('user_id');
    
    // Also check analytics_user
    if (!role) {
      const userStr = localStorage.getItem('analytics_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setUserRole(user.role);
        setContractorId(user.id);
        return;
      }
    }
    
    setUserRole(role);
    setContractorId(id);
  }, []);

  // Only show for contractors
  if (userRole !== 'contractor') {
    return null;
  }

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const startTrip = () => {
    if (!tripPurpose.trim()) {
      toast.error('Please enter trip purpose');
      return;
    }
    
    if (!navigator.geolocation) {
      toast.error('GPS not supported');
      return;
    }
    
    toast.info('Starting GPS...');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const startPos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          timestamp: new Date().toISOString()
        };
        
        setCurrentTrip({
          startTime: new Date(),
          currentMiles: 0,
          positions: [startPos]
        });
        
        setIsTracking(true);
        setIsOpen(false); // Minimize to floating button
        toast.success('Trip started!');
        
        watchIdRef.current = navigator.geolocation.watchPosition(
          (pos) => {
            setCurrentTrip(prev => {
              const newPos = {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude
              };
              
              const lastPos = prev.positions[prev.positions.length - 1];
              const distance = calculateDistance(
                lastPos.lat, lastPos.lng,
                newPos.lat, newPos.lng
              );
              
              if (distance > 0.01) {
                return {
                  ...prev,
                  currentMiles: prev.currentMiles + distance,
                  positions: [...prev.positions, newPos]
                };
              }
              return prev;
            });
          },
          (error) => {
            console.error('GPS error:', error);
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
        );
      },
      (error) => {
        toast.error('Could not get location');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const endTrip = async () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    
    const finalMiles = Math.round(currentTrip.currentMiles * 10) / 10;
    
    if (finalMiles < 0.1) {
      toast.error('Trip too short');
      setIsTracking(false);
      setCurrentTrip({ startTime: null, currentMiles: 0, positions: [] });
      return;
    }
    
    try {
      await axios.post(`${API}/contractors/${contractorId}/mileage`, {
        date: new Date().toISOString().split('T')[0],
        miles: finalMiles,
        purpose: tripPurpose,
        tracked_by_gps: true
      });
      
      const deduction = (finalMiles * 0.67).toFixed(2);
      toast.success(`Saved! ${finalMiles} mi = $${deduction}`);
      
      setIsTracking(false);
      setCurrentTrip({ startTime: null, currentMiles: 0, positions: [] });
      setTripPurpose('');
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const cancelTrip = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsTracking(false);
    setCurrentTrip({ startTime: null, currentMiles: 0, positions: [] });
    toast.info('Trip cancelled');
  };

  return (
    <>
      {/* Floating Button - Always visible for contractors */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
          isTracking 
            ? 'bg-green-500 animate-pulse' 
            : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
        }`}
      >
        {isTracking ? (
          <div className="text-center text-white">
            <Car className="w-5 h-5 mx-auto" />
            <span className="text-xs font-bold">{currentTrip.currentMiles.toFixed(1)}</span>
          </div>
        ) : (
          <Navigation className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="w-5 h-5 text-green-600" />
              GPS Mileage Tracker
            </DialogTitle>
          </DialogHeader>
          
          {!isTracking ? (
            <div className="space-y-4">
              <div>
                <Input
                  value={tripPurpose}
                  onChange={(e) => setTripPurpose(e.target.value)}
                  placeholder="Trip purpose (e.g., Service call)"
                />
              </div>
              <Button 
                onClick={startTrip}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Trip
              </Button>
              <p className="text-xs text-center text-gray-500">
                IRS Rate: $0.67/mile
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <Card className="bg-green-50">
                <CardContent className="p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-green-600 animate-pulse" />
                    <span className="text-sm text-green-700">Tracking...</span>
                  </div>
                  <p className="text-4xl font-bold text-green-600">
                    {currentTrip.currentMiles.toFixed(1)}
                  </p>
                  <p className="text-sm text-gray-600">miles</p>
                  <p className="text-xl font-semibold text-blue-600 mt-2">
                    ${(currentTrip.currentMiles * 0.67).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">estimated deduction</p>
                </CardContent>
              </Card>
              
              <p className="text-sm text-center text-gray-600">
                {tripPurpose}
              </p>
              
              <div className="flex gap-2">
                <Button 
                  onClick={endTrip}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  <StopCircle className="w-4 h-4 mr-2" />
                  End Trip
                </Button>
                <Button 
                  onClick={cancelTrip}
                  variant="outline"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GPSMileageTracker;
